import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Loader,
  Paper,
  Skeleton,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconGripVertical,
  IconHammer,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  clampFrameAspectRatio,
  frameDimensionsFromAspectRatio,
} from "../../shared/frame-placement";
import { resolvePaletteColors } from "../../shared/furniture-palette";
import type {
  ObjktToken,
  WalletNft,
} from "../../shared/types";
import type { FurnitureInventoryEntry } from "../lib/api";
import {
  fetchHeldGlbsPage,
  fetchHeldNftsPage,
  fetchTokenBalance,
  type Session,
} from "../lib/api";
import type { SelectedAsset } from "../hooks/useMarketplace";
import { FurniturePreviewCanvas } from "./FurniturePreviewCanvas";

const PANEL_WIDTH = 522;
const PANEL_HEIGHT = 548;
const SLOT_SIZE = 66;
const SLOT_COLUMNS = 6;
const SLOT_ROWS = 5;
const SLOT_COUNT = SLOT_COLUMNS * SLOT_ROWS;
const SLOT_GAP = 6;

function isVideoMime(mime?: string): boolean {
  return typeof mime === "string" && mime.toLowerCase().startsWith("video/");
}

function frameMetadataFromToken(token: WalletNft): {
  frameWidth: number;
  frameHeight: number;
  mediaMime?: string;
} {
  const aspectRatio =
    typeof token.thumbnailWidth === "number" &&
    typeof token.thumbnailHeight === "number" &&
    token.thumbnailWidth > 0 &&
    token.thumbnailHeight > 0
      ? token.thumbnailWidth / token.thumbnailHeight
      : 1;
  const dimensions = frameDimensionsFromAspectRatio(
    clampFrameAspectRatio(aspectRatio)
  );
  return {
    frameWidth: dimensions.width,
    frameHeight: dimensions.height,
    mediaMime: token.thumbnailMime ?? token.mime,
  };
}

interface Props {
  session: Session;
  furnitureInventory: FurnitureInventoryEntry[];
  onSelectFurniture: (entry: FurnitureInventoryEntry) => void;
  sculptures: ObjktToken[];
  paintings: WalletNft[];
  loadingSculptures: boolean;
  loadingPaintings: boolean;
  sculpturePage: number;
  paintingPage: number;
  sculptureHasMore: boolean;
  paintingHasMore: boolean;
  placingAsset: SelectedAsset | null;
  onSelectAsset: (asset: SelectedAsset) => void;
  onExitBuild: () => void;
  onPrevSculpturePage: () => void;
  onNextSculpturePage: () => void;
  onPrevPaintingPage: () => void;
  onNextPaintingPage: () => void;
}

function clampPanelPosition(x: number, y: number): { x: number; y: number } {
  const margin = 12;
  const maxX = Math.max(margin, window.innerWidth - PANEL_WIDTH - margin);
  const maxY = Math.max(margin, window.innerHeight - PANEL_HEIGHT - margin);
  return {
    x: Math.min(Math.max(margin, x), maxX),
    y: Math.min(Math.max(margin, y), maxY),
  };
}

function initialPanelPosition(): { x: number; y: number } {
  if (typeof window === "undefined") {
    return { x: 16, y: 84 };
  }
  return clampPanelPosition(16, window.innerHeight - PANEL_HEIGHT - 20);
}

export function BuildInventoryPanel({
  session,
  furnitureInventory,
  onSelectFurniture,
  sculptures,
  paintings,
  loadingSculptures,
  loadingPaintings,
  sculpturePage,
  paintingPage,
  sculptureHasMore,
  paintingHasMore,
  placingAsset,
  onSelectAsset,
  onExitBuild,
  onPrevSculpturePage,
  onNextSculpturePage,
  onPrevPaintingPage,
  onNextPaintingPage,
}: Props) {
  const [tab, setTab] = useState<"furniture" | "sculptures" | "paintings">(
    "furniture"
  );
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [position, setPosition] = useState(initialPanelPosition);
  const [searchInput, setSearchInput] = useState("");
  const [searchSculptureResults, setSearchSculptureResults] = useState<
    ObjktToken[]
  >([]);
  const [searchSculpturePage, setSearchSculpturePage] = useState(0);
  const [searchSculptureHasMore, setSearchSculptureHasMore] = useState(false);
  const [searchSculptureLoading, setSearchSculptureLoading] = useState(false);
  const [searchPaintingResults, setSearchPaintingResults] = useState<
    WalletNft[]
  >([]);
  const [searchPaintingPage, setSearchPaintingPage] = useState(0);
  const [searchPaintingHasMore, setSearchPaintingHasMore] = useState(false);
  const [searchPaintingLoading, setSearchPaintingLoading] = useState(false);
  const dragState = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const searchSculptureRequestSeq = useRef(0);
  const searchPaintingRequestSeq = useRef(0);

  const stopDragging = useCallback(() => {
    dragState.current = null;
    document.body.style.userSelect = "";
  }, []);

  const onDragMove = useCallback((event: PointerEvent) => {
    const state = dragState.current;
    if (!state) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    setPosition(clampPanelPosition(state.originX + dx, state.originY + dy));
  }, []);

  const onDragEnd = useCallback(
    function handleDragEnd() {
      stopDragging();
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", handleDragEnd);
      window.removeEventListener("pointercancel", handleDragEnd);
    },
    [onDragMove, stopDragging]
  );

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPanelPosition(prev.x, prev.y));
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", onDragEnd);
      window.removeEventListener("pointercancel", onDragEnd);
      stopDragging();
    };
  }, [onDragEnd, onDragMove, stopDragging]);

  const handleHeaderPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
    window.addEventListener("pointercancel", onDragEnd);
  };

  const handleSelectPainting = async (token: WalletNft) => {
    const key = `frame:${token.faContract}:${token.tokenId}`;
    const frameMeta = frameMetadataFromToken(token);
    setCheckingId(key);
    try {
      const balance = await fetchTokenBalance(
        session,
        token.faContract,
        token.tokenId
      );
      if (balance <= 0) {
        notifications.show({
          title: "Cannot place",
          message: "You don't own any editions of this token.",
          color: "red",
        });
        return;
      }
      onSelectAsset({
        type: "frame",
        url: token.thumbnailUrl,
        thumbnail: token.thumbnailUrl,
        name: token.name,
        faContract: token.faContract,
        tokenId: token.tokenId,
        ownedEditions: balance,
        mediaMime: frameMeta.mediaMime,
        frameWidth: frameMeta.frameWidth,
        frameHeight: frameMeta.frameHeight,
      });
    } catch {
      notifications.show({
        title: "Balance check failed",
        message: "Could not verify token ownership.",
        color: "orange",
      });
      onSelectAsset({
        type: "frame",
        url: token.thumbnailUrl,
        thumbnail: token.thumbnailUrl,
        name: token.name,
        faContract: token.faContract,
        tokenId: token.tokenId,
        ownedEditions: 0,
        mediaMime: frameMeta.mediaMime,
        frameWidth: frameMeta.frameWidth,
        frameHeight: frameMeta.frameHeight,
      });
    } finally {
      setCheckingId(null);
    }
  };

  const isSelectedPainting = (token: WalletNft) =>
    placingAsset?.type === "frame" &&
    placingAsset.faContract === token.faContract &&
    placingAsset.tokenId === token.tokenId;

  const normalizedSearch = searchInput.trim();
  const searchActive = normalizedSearch.length >= 2;

  const runSculptureSearch = useCallback(
    async (queryText: string, page: number) => {
      const trimmed = queryText.trim();
      const nextPage = Math.max(0, page);
      if (trimmed.length < 2) {
        setSearchSculptureResults([]);
        setSearchSculpturePage(0);
        setSearchSculptureHasMore(false);
        setSearchSculptureLoading(false);
        return;
      }

      const requestId = ++searchSculptureRequestSeq.current;
      setSearchSculptureLoading(true);
      try {
        const result = await fetchHeldGlbsPage(session, {
          search: trimmed,
          limit: SLOT_COUNT,
          offset: nextPage * SLOT_COUNT,
        });
        if (requestId !== searchSculptureRequestSeq.current) return;
        setSearchSculptureResults(result.tokens);
        setSearchSculpturePage(nextPage);
        setSearchSculptureHasMore(result.pagination.hasMore);
      } catch {
        if (requestId !== searchSculptureRequestSeq.current) return;
        setSearchSculptureResults([]);
        setSearchSculptureHasMore(false);
        setSearchSculpturePage(0);
        notifications.show({
          title: "Search failed",
          message: "Could not search your owned sculptures right now.",
          color: "orange",
        });
      } finally {
        if (requestId === searchSculptureRequestSeq.current) {
          setSearchSculptureLoading(false);
        }
      }
    },
    [session]
  );

  const runPaintingSearch = useCallback(
    async (queryText: string, page: number) => {
      const trimmed = queryText.trim();
      const nextPage = Math.max(0, page);
      if (trimmed.length < 2) {
        setSearchPaintingResults([]);
        setSearchPaintingPage(0);
        setSearchPaintingHasMore(false);
        setSearchPaintingLoading(false);
        return;
      }

      const requestId = ++searchPaintingRequestSeq.current;
      setSearchPaintingLoading(true);
      try {
        const result = await fetchHeldNftsPage(session, {
          search: trimmed,
          limit: SLOT_COUNT,
          offset: nextPage * SLOT_COUNT,
        });
        if (requestId !== searchPaintingRequestSeq.current) return;
        setSearchPaintingResults(result.tokens);
        setSearchPaintingPage(nextPage);
        setSearchPaintingHasMore(result.pagination.hasMore);
      } catch {
        if (requestId !== searchPaintingRequestSeq.current) return;
        setSearchPaintingResults([]);
        setSearchPaintingHasMore(false);
        setSearchPaintingPage(0);
        notifications.show({
          title: "Search failed",
          message: "Could not search your owned paintings right now.",
          color: "orange",
        });
      } finally {
        if (requestId === searchPaintingRequestSeq.current) {
          setSearchPaintingLoading(false);
        }
      }
    },
    [session]
  );

  useEffect(() => {
    if (normalizedSearch.length < 2) {
      searchSculptureRequestSeq.current += 1;
      searchPaintingRequestSeq.current += 1;
      setSearchSculptureLoading(false);
      setSearchSculptureResults([]);
      setSearchSculpturePage(0);
      setSearchSculptureHasMore(false);
      setSearchPaintingLoading(false);
      setSearchPaintingResults([]);
      setSearchPaintingPage(0);
      setSearchPaintingHasMore(false);
      return;
    }
    const timeout = window.setTimeout(() => {
      if (tab === "sculptures") {
        void runSculptureSearch(normalizedSearch, 0);
      } else {
        void runPaintingSearch(normalizedSearch, 0);
      }
    }, 260);
    return () => window.clearTimeout(timeout);
  }, [normalizedSearch, tab, runSculptureSearch, runPaintingSearch]);

  const shownSculptures = searchActive ? searchSculptureResults : sculptures;
  const shownPaintings = searchActive ? searchPaintingResults : paintings;
  const sculpturesLoading = searchActive
    ? searchSculptureLoading
    : loadingSculptures;
  const paintingsLoading = searchActive
    ? searchPaintingLoading
    : loadingPaintings;

  const currentLoading =
    tab === "sculptures" ? sculpturesLoading : paintingsLoading;
  const currentPage =
    tab === "sculptures"
      ? searchActive
        ? searchSculpturePage
        : sculpturePage
      : searchActive
      ? searchPaintingPage
      : paintingPage;
  const canPrev = currentPage > 0;
  const canNext =
    tab === "sculptures"
      ? searchActive
        ? searchSculptureHasMore
        : sculptureHasMore
      : searchActive
      ? searchPaintingHasMore
      : paintingHasMore;
  const iconItems = tab === "sculptures" ? shownSculptures : shownPaintings;
  const sculptureEmptySlots = Math.max(0, SLOT_COUNT - shownSculptures.length);
  const paintingEmptySlots = Math.max(0, SLOT_COUNT - shownPaintings.length);

  return (
    <Paper
      p={10}
      radius="md"
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        width: PANEL_WIDTH,
        height: PANEL_HEIGHT,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        border: "1px solid rgba(0,0,0,0.10)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        pointerEvents: "auto",
      }}
    >
      <Box
        onPointerDown={handleHeaderPointerDown}
        style={{
          touchAction: "none",
          cursor: "grab",
          marginBottom: 6,
          padding: "6px 10px",
          userSelect: "none",
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <IconGripVertical size={14} color="rgba(0,0,0,0.3)" />
            <IconHammer size={15} color="#ff922b" />
            <Title order={5} fz={13} c="gray.8">
              Build Inventory
            </Title>
            {placingAsset && (
              <Badge size="xs" variant="light" color="teal">
                {placingAsset.name ?? "Selected"}
              </Badge>
            )}
          </Group>
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray.4"
            onClick={onExitBuild}
          >
            <IconX size={15} />
          </ActionIcon>
        </Group>
      </Box>

      {tab === "paintings" ? (
        <TextInput
          value={searchInput}
          onChange={(event) => setSearchInput(event.currentTarget.value)}
          size="xs"
          placeholder="Search owned paintings: name, description, artist"
          leftSection={<IconSearch size={14} />}
          mb={10}
        />
      ) : null}

      <Tabs
        value={tab}
        onChange={(value) =>
          setTab(
            (value as "furniture" | "sculptures" | "paintings") ?? "furniture"
          )
        }
        style={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Tabs.List>
          <Tabs.Tab value="furniture">Furniture</Tabs.Tab>
          <Tabs.Tab value="paintings">Paintings</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel
          value="furniture"
          pt={8}
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <Box
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: `repeat(${SLOT_COLUMNS}, ${SLOT_SIZE}px)`,
              gap: SLOT_GAP,
              justifyContent: "center",
              alignContent: "start",
            }}
          >
            {furnitureInventory.map((entry) => {
              const item = entry.item;
              const variantId = entry.colorVariantId?.trim() ?? "";
              const resolvedVariantId = variantId || undefined;
              const resolvedPaletteColors =
                entry.paletteColors &&
                Object.keys(entry.paletteColors).length > 0
                  ? entry.paletteColors
                  : resolvePaletteColors(
                      item.colorVariants,
                      resolvedVariantId,
                      item.defaultColorVariantId
                    );
              const variantName = variantId
                ? item.colorVariants?.find((variant) => variant.id === variantId)
                    ?.name ?? variantId
                : null;
              const selected =
                placingAsset?.type === "glb" &&
                "furnitureId" in placingAsset &&
                placingAsset.furnitureId === item.id &&
                ((placingAsset.colorVariantId?.trim() ?? "") === variantId);
              return (
                <Tooltip
                  key={`${item.id}:${variantId || "default"}`}
                  label={
                    variantName
                      ? `${item.name} - ${variantName} (${entry.quantity} available)`
                      : `${item.name} (${entry.quantity} available)`
                  }
                  withArrow
                  openDelay={120}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onSelectFurniture(entry)}
                    style={{
                      width: SLOT_SIZE,
                      height: SLOT_SIZE,
                      borderRadius: 8,
                      overflow: "hidden",
                      position: "relative",
                      border: selected
                        ? "2px solid #20c997"
                        : "1px solid rgba(0,0,0,0.10)",
                      background: selected
                        ? "rgba(32,201,151,0.10)"
                        : "rgba(0,0,0,0.02)",
                      padding: 0,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition:
                        "border-color 120ms ease, background 120ms ease",
                    }}
                  >
                    {item.modelUrl?.toLowerCase().endsWith(".glb") ? (
                      <FurniturePreviewCanvas
                        modelUrl={item.modelUrl}
                        tintColor={item.tintColor}
                        colorVariants={item.colorVariants}
                        defaultColorVariantId={item.defaultColorVariantId}
                        colorVariantId={resolvedVariantId}
                        paletteColors={resolvedPaletteColors}
                      />
                    ) : item.thumbnailUrl ? (
                      <img
                        src={item.thumbnailUrl}
                        alt={item.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      <Text size="xs" c="dimmed" ta="center" px={2}>
                        {item.name}
                      </Text>
                    )}
                    <Box
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        borderRadius: 6,
                        border: "1px solid rgba(0,0,0,0.15)",
                        background: "rgba(255,255,255,0.92)",
                        color: "#212529",
                        fontSize: 10,
                        fontWeight: 700,
                        lineHeight: 1,
                        padding: "2px 4px",
                        pointerEvents: "none",
                      }}
                    >
                      [{entry.quantity}]
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
            {Array.from({
              length: Math.max(0, SLOT_COUNT - furnitureInventory.length),
            }).map((_, index) => (
              <Box
                key={`empty-furniture-${index}`}
                style={{
                  width: SLOT_SIZE,
                  height: SLOT_SIZE,
                  borderRadius: 8,
                  border: "1px solid rgba(0,0,0,0.06)",
                  background: "rgba(0,0,0,0.01)",
                }}
              />
            ))}
          </Box>
          {furnitureInventory.length === 0 && (
            <Text size="sm" c="gray.5" ta="center" mb={6}>
              No furniture available to place. Buy or unplace furniture first.
            </Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel
          value="paintings"
          pt={8}
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <Box
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: `repeat(${SLOT_COLUMNS}, ${SLOT_SIZE}px)`,
              gap: SLOT_GAP,
              justifyContent: "center",
              alignContent: "start",
            }}
          >
            {paintingsLoading &&
              Array.from({ length: SLOT_COUNT }).map((_, index) => (
                <Skeleton
                  key={`painting-skeleton-${index}`}
                  width={SLOT_SIZE}
                  height={SLOT_SIZE}
                  radius="sm"
                />
              ))}
            {!paintingsLoading &&
              shownPaintings.map((token) => {
                const key = `frame:${token.faContract}:${token.tokenId}`;
                const selected = isSelectedPainting(token);
                const checking = checkingId === key;
                const ownedCount = token.ownedEditions;
                return (
                  <Tooltip
                    key={key}
                    label={token.name}
                    withArrow
                    openDelay={120}
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={() => {
                        if (!checking) void handleSelectPainting(token);
                      }}
                      style={{
                        width: SLOT_SIZE,
                        height: SLOT_SIZE,
                        borderRadius: 8,
                        overflow: "hidden",
                        position: "relative",
                        border: selected
                          ? "2px solid #20c997"
                          : "1px solid rgba(0,0,0,0.10)",
                        background: selected
                          ? "rgba(32,201,151,0.10)"
                          : "rgba(0,0,0,0.02)",
                        padding: 0,
                        cursor: checking ? "wait" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: checking ? 0.7 : 1,
                        transition:
                          "border-color 120ms ease, background 120ms ease",
                      }}
                    >
                      {checking ? (
                        <Loader size="sm" color="orange" />
                      ) : isVideoMime(token.thumbnailMime ?? token.mime) ? (
                        <video
                          src={token.thumbnailUrl}
                          muted
                          autoPlay
                          loop
                          playsInline
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <img
                          src={token.thumbnailUrl}
                          alt={token.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      )}
                      {!checking && typeof ownedCount === "number" && (
                        <Box
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            borderRadius: 6,
                            border: "1px solid rgba(0,0,0,0.15)",
                            background: "rgba(255,255,255,0.92)",
                            color: "#212529",
                            fontSize: 10,
                            fontWeight: 700,
                            lineHeight: 1,
                            padding: "2px 4px",
                            pointerEvents: "none",
                          }}
                        >
                          [{ownedCount}]
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            {!paintingsLoading &&
              Array.from({ length: paintingEmptySlots }).map((_, index) => (
                <Box
                  key={`empty-painting-${index}`}
                  style={{
                    width: SLOT_SIZE,
                    height: SLOT_SIZE,
                    borderRadius: 8,
                    border: "1px solid rgba(0,0,0,0.06)",
                    background: "rgba(0,0,0,0.01)",
                  }}
                />
              ))}
          </Box>
          {!paintingsLoading && shownPaintings.length === 0 && (
            <Text size="sm" c="gray.5" ta="center" mb={6}>
              {searchActive
                ? `No owned painting results for "${normalizedSearch}".`
                : "No NFTs on this page."}
            </Text>
          )}
        </Tabs.Panel>
      </Tabs>

      {tab === "paintings" ? (
        <Box mt={8} px={8} py={7}>
          <Group justify="space-between" wrap="nowrap">
            <Group gap={6} wrap="nowrap">
              <ActionIcon
                variant="default"
                size="sm"
                disabled={currentLoading || !canPrev}
                onClick={() => {
                  if (searchActive) {
                    void runPaintingSearch(
                      normalizedSearch,
                      searchPaintingPage - 1
                    );
                  } else {
                    onPrevPaintingPage();
                  }
                }}
              >
                <IconChevronLeft size={14} />
              </ActionIcon>
              <Text size="xs" c="gray.4">
                {searchActive ? "Search" : "Page"} {currentPage + 1}
              </Text>
              <ActionIcon
                variant="default"
                size="sm"
                disabled={currentLoading || !canNext}
                onClick={() => {
                  if (searchActive) {
                    void runPaintingSearch(
                      normalizedSearch,
                      searchPaintingPage + 1
                    );
                  } else {
                    onNextPaintingPage();
                  }
                }}
              >
                <IconChevronRight size={14} />
              </ActionIcon>
            </Group>
            <Text size="xs" c="gray.5">
              {currentLoading
                ? "Loading..."
                : `${iconItems.length}/${SLOT_COUNT} slots`}
            </Text>
          </Group>
        </Box>
      ) : null}
    </Paper>
  );
}
